import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { publicSettings } from "@/lib/api";

const DEFAULTS = {
  enabled: true,
  scroll_threshold_percent: 70,
  time_threshold_seconds: 45,
  free_detail_clicks: 2,
};

const STORAGE_KEY = "rp_paywall_v1";

function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeState(patch) {
  const next = { ...readState(), ...patch };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

/**
 * Soft-paywall — fires on any of (scroll, time, detail-click count).
 * Returns { triggered, reason, registerDetailClick, dismiss, config }.
 *
 * When `triggered=true` the page should render the <PaywallModal />.
 */
export default function usePaywall({ enableScroll = true, enableTime = true } = {}) {
  const { user } = useAuth();
  const [config, setConfig] = useState(DEFAULTS);
  const [triggered, setTriggered] = useState(false);
  const [reason, setReason] = useState(null);

  // Load admin-configured thresholds once.
  useEffect(() => {
    publicSettings
      .get()
      .then((d) => {
        if (d?.paywall) setConfig({ ...DEFAULTS, ...d.paywall });
      })
      .catch(() => {});
  }, []);

  // Always cleared for authenticated users.
  useEffect(() => {
    if (user) setTriggered(false);
  }, [user]);

  const fire = useCallback(
    (why) => {
      if (user || !config.enabled || triggered) return;
      setReason(why);
      setTriggered(true);
    },
    [user, config.enabled, triggered]
  );

  // Scroll trigger
  useEffect(() => {
    if (!enableScroll || user || !config.enabled) return;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = (window.scrollY / max) * 100;
      if (pct >= config.scroll_threshold_percent) fire("scroll");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [enableScroll, user, config.enabled, config.scroll_threshold_percent, fire]);

  // Time trigger
  useEffect(() => {
    if (!enableTime || user || !config.enabled) return;
    const t = setTimeout(() => fire("time"), config.time_threshold_seconds * 1000);
    return () => clearTimeout(t);
  }, [enableTime, user, config.enabled, config.time_threshold_seconds, fire]);

  // Detail-click counter
  const registerDetailClick = useCallback(() => {
    if (user || !config.enabled) return false;
    const state = readState();
    const used = (state.detail_clicks || 0) + 1;
    writeState({ detail_clicks: used });
    if (used > config.free_detail_clicks) {
      fire("detail");
      return true; // paywall just triggered → block the action
    }
    return false;
  }, [user, config.enabled, config.free_detail_clicks, fire]);

  const dismiss = useCallback(() => setTriggered(false), []);

  return { triggered, reason, registerDetailClick, dismiss, config };
}
